const post = {
  slug: 'building-rag-in-production',
  number: 5,
  title: 'What Building RAG in Production Actually Taught Me',
  date: '2026-06-12',
  readTime: '8 min read',
  views: 109,
  category: 'ai',
  tags: ['AI/ML', 'RAG', 'Backend'],
  excerpt:
    'Anyone can build a RAG demo in an afternoon. Keeping one healthy in front of real users is a different job entirely. This is the whole thing, from the bug that started it to the checklist I run now.',
  cover: null,
  content: `
The first time our support RAG system gave a customer a wrong answer with complete confidence, it was about a fee that had changed two weeks earlier. The model was not hallucinating in the way people mean when they wave the word around. It had been handed the old policy text, verbatim, by our own retrieval layer, and it did exactly what it was told. The answer was fluent, well-formatted, and wrong, and it went out under our name.

That is the whole story of RAG in production, compressed into one incident. The demo is easy. You load some documents, drop them into a vector store, wire up a model, and ask it questions, and it works on the first try and feels like magic. Then you put it in front of real users at Stax, real questions, real money on the line, and you learn that almost none of the hard parts are the parts everyone talks about.

This is the full version of what I learned, in the order it actually mattered.

## Retrieval is the whole game, not the model

People love arguing about which model to use. Spend a week in a real RAG system and you stop caring nearly as much. The model is the last step, and it is mostly downstream of decisions made long before the prompt. What you put into the context window matters far more than which model reads it.

I can make this concrete. We ran the same questions through a stronger and a weaker model and the answers barely moved. We changed how we chunked and filtered the documents, kept the model fixed, and the quality jumped. The lesson stuck: when a RAG answer is bad, the model is usually innocent. The retrieval handed it garbage, and a smarter model just produces a more articulate version of the wrong answer.

So I stopped treating retrieval as plumbing and started treating it as the product.

## Chunk on meaning, not on token count

The default way to split documents is to cut every few hundred tokens. It is easy and it is quietly terrible. Fixed windows slice through the middle of a sentence, an instruction, a table, and the model fills the gap with something plausible.

We replaced that with chunking that respected the structure of our help content: split on headings and paragraphs, keep a section together, and never break in the middle of a thought. It is less elegant than a clean token count and it worked far better, because every chunk was a complete idea the model could actually use.

\`\`\`text
Bad:   [...the refund is processed within 5] [business days unless the account...]
Good:  [Refunds are processed within 5 business days unless the account is flagged.]
\`\`\`

If you change one thing after reading this, change how you chunk. It is the cheapest large win available.

## Filter before you search, not after

The second big lever was metadata. We tagged every chunk with its source, its age, and its document type, and then we filtered on that metadata *before* running the similarity search, not after.

\`\`\`python
# Narrow the candidate set first, then do similarity search on what's left
results = index.query(
    vector=embed(question),
    top_k=8,
    filter={"doc_type": "policy", "active": True},
)
\`\`\`

The order is the whole point. Searching everything and hoping the right thing floats to the top means a retired policy from two years ago competes with the current one on pure similarity, and sometimes it wins. Filtering first means the search only ever sees candidates that are allowed to be right. That single change did more for answer quality than any prompt I wrote.

## Stale context is the silent failure

A vector store is a snapshot, and the world is not. The moment a policy changes, every chunk you indexed from the old version becomes a small landmine, waiting to hand the model outdated text with full confidence. That fee incident I opened with was exactly this.

There is no clever model fix for stale data. The fix is operational discipline. Every document carried a last-updated date. Anything past a freshness threshold got flagged for re-indexing, and crucially, the pipeline that owned the source content also owned pushing updates into the index. Retrieval quality is not a one-time setup you finish. It is an ongoing concern, the same as a database that needs its migrations run.

## Measure grounding, not vibes

For a while we evaluated the way everyone does at first: someone read a handful of answers and decided they looked good. That does not survive contact with real traffic. You cannot hold the edge cases in your head, and "looked good" silently drifts.

So we stopped tracking how good answers felt and started tracking whether each answer was actually supported by the text we retrieved. A grounded-answer rate. Of the answers we gave, how many were backed by the context, yes or no. The moment that number became the thing we optimized, both the prompts and the retrieval got better, because we were finally measuring the failure we actually cared about.

\`\`\`text
Judge rubric (one question, not a vague "rate quality"):
  Is every claim in the answer supported by the provided context?  yes / no
\`\`\`

> The goal was never to answer everything. It was to never invent anything.

We also gave the model an explicit way out. If the retrieved context did not contain the answer, the correct response was to say so and route to a human. That sounds obvious, and you still have to design for it, measure it, and reward it, or the model will cheerfully guess instead of admitting the gap.

## Treat the model call like any other dependency

The thing that made our RAG system reliable was boring. We treated the model call as exactly what it is: one node in a normal distributed system, with all the discipline that implies.

1. **Timeouts and retries** on every call to the provider.
2. **A circuit breaker** so a slow provider does not drag the whole request path down with it.
3. **Caching** for repeated questions, which is cheaper and faster, and at support volume there are a lot of repeats.
4. **A fallback** for when the model is simply unavailable, so the feature degrades instead of dying.

None of this is AI-specific. It is the same engineering you would apply to any flaky third-party API, and the teams that skip it because the model feels special are the ones paged at 2am.

## Log what you retrieved, or debug blind

This is the piece almost everyone skips, and it is the one I would fight for hardest. Log the chunks you retrieved, not just the final answer.

When a RAG answer is wrong, the answer text tells you almost nothing about why. The interesting question is what the model was looking at, and if you did not log the retrieved context, you cannot answer it. You end up tweaking prompts for an afternoon when the real problem was three stale documents upstream that you could have seen in ten seconds.

We logged, per query, the question, the retrieved chunks with their metadata, and the final answer. The first time a bad answer investigation ended in two minutes instead of two hours, the logging had paid for itself.

## A production checklist

This is roughly what I run through now before a RAG feature goes near real users:

- Chunks split on meaning, never on raw token count.
- Metadata on every chunk, and filtering applied before the similarity search.
- A freshness story: documents have dates, and stale ones get re-indexed automatically.
- A grounded-answer metric wired up and watched, not just spot-checked.
- An explicit "I do not know" path that routes to a human.
- Timeouts, retries, a circuit breaker, and a fallback around the model call.
- Caching for repeated questions.
- Retrieval logging, so a bad answer is debuggable.

## What I would do first next time

If I started this project again tomorrow, I would not begin with the model, the prompts, or the vector database. I would begin with two things on day one: a way to measure whether an answer is grounded in the source, and logging for whatever the retriever returns on every single query.

Everything downstream gets easier once you can see what the system is actually doing. The model is the part everyone talks about. The retrieval, the freshness, and the observability are the parts that decide whether real users trust the thing, and trust is the only metric that matters once money is involved.
`,
};

export default post;
